import { createContext, Dispatch, SetStateAction, useCallback, useEffect, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  ContactInfo,
  CustomerAppointmentDTO,
  ProjectDTO2,
  TaskComponentAttributeValueDTO,
  TaskComponentDTO,
  TaskComponentValueDTO,
  UpdateAttributeValueDTO,
  UpdateWorkTaskDTO2,
  WorkOrderInfoDto
} from '../../../../api/api';
import { TabProps } from '../../../../blocks/tabs-vertical/TabsVertical';
import TaskTypeTemplateComponent from '../../../../blocks/tasktype-template-component/TaskTypeTemplateComponent';
import Button from '../../../../components/button/Button';
import {
  MappedProjectNumber,
  projectNumberToOption
} from '../../../../components/project-number-dropdown/ProjectNumberDropdown';
import { useConfirmationDialog } from '../../../../hooks/useConfirmationDialog';
import useFormIsDirty from '../../../../hooks/useFormIsDirty';
import { WorkTaskDTOExtended } from '../../../../models/TaskType';
import { TaskComponent } from '../../../../models/TaskTypeComponent';
import NotificationService from '../../../../services/NotificationService';
import ProjectsService from '../../../../services/ProjectsService';
import TaskService from '../../../../services/TaskService';
import { DialogBody } from '../../../../stateManagement/reducers/confirmDialogReducer';
import { setListShouldUpdate } from '../../../../stateManagement/reducers/taskListReducer';
import { Container, Section, SectionContent, SectionHeader } from '../../../../styling/FormStyling';
import { StyledCircularProgress } from '../../../../styling/Styling';
import { locationsAreEqual } from '../../../../utils/location/locationHandling';
import { log } from '../../../../utils/logging/log';
import TextRow from '../components/TextRow';
import { CustomerAppointment } from './components/CustomerAppointment';
import { Information } from './components/Information';
import { SubTasks } from './components/Subtasks';
import { ExternalResources } from './components/ExternalResources';
import { DetailStep, getInitialState, PayloadAction, reducer, TaskErrors } from './TaskDetailsReducer';
import ContactPersons from './ContactPersons';
import { MeterDetails } from './components/MeterDetails';
import { Details } from './components/Details';
import NearbyTasks from '../../../../blocks/nearby-tasks/NearbyTasks';
import { AccessConditions } from './components/AccessConditions';
import TaskRelationsDiagram from '../../../../blocks/task-relations-diagram/TaskRelationsDiagram';
import useFeatureFlag from '../../../../hooks/useFeatureFlag';

interface Props extends TabProps {
  task?: WorkTaskDTOExtended;
  customerAppointment?: CustomerAppointmentDTO;
  editable: boolean;
  setEditable: Dispatch<SetStateAction<boolean>>;
  setWorkTaskCallback: (task: WorkTaskDTOExtended) => void;
  scrollIntoPreviousPosition: (id: string) => void;
  workInvoice?: number;
  workInvoiceProjectNumber?: string;
  isUpdatingTask?: boolean;
  projectNumberObject?: MappedProjectNumber;
  contactPersons?: ContactInfo[];
  addContactCallback: (newContact: ContactInfo) => void;
  meterDetails?: WorkOrderInfoDto;
}

export const MeterTaskDetailsContext = createContext<DetailStep>({});
export const MeterTaskDetailsDispatchContext = createContext<React.Dispatch<PayloadAction>>(() => {});

const MeterTaskDetailsStep = (props: Props) => {
  const showRelationshipMap = useFeatureFlag(window._env_.FeatureFlags_RelationshipMap);
  const {
    editable,
    setEditable,
    setWorkTaskCallback,
    tabId,
    scrollIntoPreviousPosition,
    workInvoice,
    workInvoiceProjectNumber,
    contactPersons,
    addContactCallback,
    meterDetails
  } = props;

  const dispatch = useDispatch();
  const [taskDetails, dispatchTaskDetails] = useReducer(reducer, getInitialState(props.task));
  const {
    task,
    projectNumberAndName,
    components,
    customerAppointment,
    deadline,
    department,
    description,
    dynamicFieldErrors,
    earliestStartDate,
    externalProjects,
    isProjectNumberEditable,
    isUpdatingTask,
    notesForPlanning,
    projectNumberObject,
    remainingTime,
    stretches,
    taskLocation,
    workTaskErrors,
    isAccessConditionsLoading
  } = taskDetails;
  const { getConfirmation } = useConfirmationDialog();
  const { isDirty, toggleFieldIsDirty, resetFields } = useFormIsDirty();

  useEffect(() => {
    dispatchTaskDetails({ type: 'initialize', payload: getInitialState(props.task) });
  }, [props.task]);

  useEffect(() => {
    if (!props.task?.components) return;
    const sortedComponents = [...props.task.components]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((c) => {
        return {
          ...c,
          attributes: c.attributes?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        };
      });
    dispatchTaskDetails({ type: 'set-components', payload: sortedComponents });
  }, [props.task?.components]);

  useEffect(() => {
    if (projectNumberAndName === '-' && task?.projectNumber) {
      TaskService.getProjectById(task.projectNumber)
        .then((res) => {
          dispatchTaskDetails({ type: 'set-project-number-and-name', payload: `${res.id} - ${res.name}` });
          dispatchTaskDetails({ type: 'set-project-manager', payload: res.projectManager });
        })
        .catch((err) => {
          dispatchTaskDetails({ type: 'set-project-number-and-name', payload: task?.projectNumber ?? '' });
          log(err);
        })
        .finally(() => {
          tabId && scrollIntoPreviousPosition(tabId);
        });
    }
  }, [dispatchTaskDetails, scrollIntoPreviousPosition, tabId, projectNumberAndName, task?.projectNumber]);

  useEffect(() => {
    if ((externalProjects ?? []).length === 0 && task && (editable || isProjectNumberEditable)) {
      dispatchTaskDetails({ type: 'set-is-loading-project-numbers', payload: true });
      ProjectsService.getProjectsByCategory(undefined)
        .then((_externalProjects: ProjectDTO2[]) => {
          dispatchTaskDetails({ type: 'set-external-projects', payload: _externalProjects });
          if (task?.projectNumber) {
            const selectedProject = _externalProjects.find((p) => p.id === task?.projectNumber);
            selectedProject &&
              dispatchTaskDetails({ type: 'set-project-number-object', payload: projectNumberToOption(selectedProject) });
          }
        })
        .catch((err) => {
          log(err);
        })
        .finally(() => {
          dispatchTaskDetails({ type: 'set-is-loading-project-numbers', payload: false });
        });
    }
  }, [editable, isProjectNumberEditable, dispatchTaskDetails, externalProjects, task]);

  const handleChangeDynamicField = useCallback(
    (componentId: number, fieldId: number, value: any) => {
      toggleFieldIsDirty('dynamicFields', true);

      //Both components and attributes arrays and specific attribute must be copied

      if (!components) return;

      const comp = components.find((c: TaskComponentValueDTO) => c.id === componentId);
      if (!comp || !comp.attributes) return;

      let newComp = { ...comp, attributes: [...comp?.attributes] };
      const att = newComp.attributes?.find((a: TaskComponentAttributeValueDTO) => a.attributeId === fieldId);
      const attIndex = newComp.attributes?.findIndex((a: TaskComponentAttributeValueDTO) => a.attributeId === fieldId);

      if (att) {
        newComp.attributes[attIndex] = { ...att, value };
      }

      dispatchTaskDetails({
        type: 'set-components',
        payload: components.map((c: TaskComponentValueDTO) => {
          return c.id === newComp.id ? newComp : c;
        })
      });
    },
    [dispatchTaskDetails, components, toggleFieldIsDirty]
  );

  const handleValidateTask = (): TaskErrors => {
    let errors: TaskErrors = {};

    if (!description) errors.description = true;

    return errors;
  };

  const getNewWorkTaskDto = (componentAttributes?: UpdateAttributeValueDTO[]) => {
    const { startDate, endDate } = customerAppointment ?? {};
    const newStartDate = customerAppointment?.approvedByPlanning ? startDate : earliestStartDate;
    const newEndDate = customerAppointment?.approvedByPlanning ? endDate : deadline;

    return {
      description: description !== task?.description ? description : undefined,
      assignedToDepartmentId: department !== task?.assignedToDepartment ? department?.departmentId : undefined,
      earliestStartDate: newStartDate !== task?.earliestStartDate ? newStartDate : undefined,
      deadline: newEndDate !== task?.deadline ? newEndDate : undefined,
      customerAppointment,
      remainingTimeMin: remainingTime !== task?.remainingTimeMin ? remainingTime : undefined,
      taskLocation: !locationsAreEqual(taskLocation, task?.taskLocation ?? {}) ? taskLocation : undefined,
      notesForPlanning: notesForPlanning !== task?.notesForPlanning ? notesForPlanning : undefined,
      componentAttributes,
      projectLegalEntityId:
        projectNumberObject?.value.legalEntityId !== task?.projectLegalEntityId
          ? projectNumberObject?.value.legalEntityId
          : undefined,
      projectNumber: projectNumberObject?.value.id !== task?.projectNumber ? projectNumberObject?.value.id : undefined,
      stretches,
      workTaskResponsibleEmail:
        props.task?.workTaskResponsibleEmail !== task?.workTaskResponsibleEmail ? task?.workTaskResponsibleEmail : undefined,
      workTaskResponsibleName:
        props.task?.workTaskResponsibleName !== task?.workTaskResponsibleName ? task?.workTaskResponsibleName : undefined,
      assignedToEmail: props.task?.assignedToEmail !== task?.assignedToEmail ? task?.assignedToEmail : undefined,
      assignedToName: props.task?.assignedToName !== task?.assignedToName ? task?.assignedToName : undefined
    } as UpdateWorkTaskDTO2;
  };

  const saveUpdatedTask = () => {
    if (!task?.id) return;
    if (
      earliestStartDate &&
      deadline &&
      new Date(earliestStartDate).setHours(0, 0, 0, 0) > new Date(deadline).setHours(0, 0, 0, 0)
    ) {
      NotificationService.error('Startdatoen ligger efter slutdatoen. Ændre dette og prøv igen');
      return;
    }

    const workTaskErrors = handleValidateTask();
    dispatchTaskDetails({ type: 'set-work-task-errors', payload: workTaskErrors });

    let mandatoryFieldsFilled = true;

    const componentAttributes = components?.reduce(
      (acc: any, component: TaskComponentValueDTO): UpdateAttributeValueDTO[] => {
        component.attributes?.forEach((a) => {
          if (a.isMandatory && !a.value) mandatoryFieldsFilled = false;

          if (a.isEditable) acc.push({ value: a.value, id: a.id });
        });
        return acc;
      },
      [] as UpdateAttributeValueDTO[]
    );

    if (!mandatoryFieldsFilled) {
      dispatchTaskDetails({ type: 'set-dynamic-field-errors', payload: true });
    }

    if (!mandatoryFieldsFilled || Object.keys(workTaskErrors).length) return;

    const diff = getNewWorkTaskDto(componentAttributes);
    dispatchTaskDetails({ type: 'set-is-updating-task', payload: true });

    TaskService.updateWorkTask(parseInt(task?.id), diff)
      .then((newTask) => {
        setWorkTaskCallback(newTask);
        dispatchTaskDetails({ type: 'set-customer-appointment', payload: newTask.customerAppointment });
        setEditable(false);
        resetFields();
        dispatch(setListShouldUpdate(true));
        NotificationService.success('Opgaven blev opdateret.');
      })
      .catch((err) => {
        log(err);
        NotificationService.error('Der opstod en fejl i opdateringen af arbejdskortet - ændringerne er ikke gemt');
      })
      .finally(() => {
        dispatchTaskDetails({ type: 'set-is-updating-task', payload: false });
      });
  };

  const cancelChanges = async () => {
    if (!isDirty) {
      setEditable(false);
      dispatchTaskDetails({ type: 'set-is-project-number-editable', payload: false });
      dispatchTaskDetails({ type: 'set-work-task-errors', payload: {} });
      dispatchTaskDetails({ type: 'set-appointment-errors', payload: {} });
      return;
    }

    const confirmation = await getConfirmation(dialogBody);
    if (confirmation === 'confirm') {
      dispatchTaskDetails({ type: 'set-components', payload: task?.components });
      dispatchTaskDetails({ type: 'set-description', payload: task?.description });
      dispatchTaskDetails({ type: 'set-department', payload: task?.assignedToDepartment });
      dispatchTaskDetails({ type: 'set-earliest-start-date', payload: task?.earliestStartDate });
      dispatchTaskDetails({ type: 'set-deadline', payload: task?.deadline });
      dispatchTaskDetails({ type: 'set-customer-appointment', payload: task?.customerAppointment });
      dispatchTaskDetails({ type: 'set-notes-for-planning', payload: task?.notesForPlanning });
      dispatchTaskDetails({ type: 'set-task-location', payload: task?.taskLocation });
      dispatchTaskDetails({ type: 'set-is-project-number-editable', payload: false });
      setEditable(false);
      const selectedProject = (externalProjects ?? []).find((p: ProjectDTO2) => p.id === task?.projectNumber);
      selectedProject &&
        dispatchTaskDetails({ type: 'set-project-number-object', payload: projectNumberToOption(selectedProject) });

      dispatchTaskDetails({ type: 'set-work-task-errors', payload: {} });
      dispatchTaskDetails({ type: 'set-appointment-errors', payload: {} });
      dispatchTaskDetails({
        type: 'set-task',
        payload: {
          ...task,
          workTaskResponsibleEmail: props.task?.workTaskResponsibleEmail,
          workTaskResponsibleName: props.task?.workTaskResponsibleName,
          assignedToName: props.task?.assignedToName,
          assignedToEmail: props.task?.assignedToEmail
        }
      });

      resetFields();
    }
  };

  return (
    <MeterTaskDetailsContext.Provider value={taskDetails}>
      <MeterTaskDetailsDispatchContext.Provider value={dispatchTaskDetails}>
        <Container>
          <Information
            toggleFieldIsDirty={toggleFieldIsDirty}
            editable={editable}
            task={task}
            onTaskUpdated={setWorkTaskCallback}
          />
          <SubTasks task={task} />
          <Section>
            <SectionHeader>Arbejdsbeskrivelse{editable && '*'}</SectionHeader>
            <StyledSectionContent>
              <TextRow
                inputMode={editable}
                type="multilineText"
                label="Arbejdsbeskrivelse"
                value={task?.description ?? '-'}
                onBlur={(text: string) => dispatchTaskDetails({ type: 'set-description', payload: text })}
                isDirty={toggleFieldIsDirty}
                error={workTaskErrors?.description}
                hideLabel
              />
            </StyledSectionContent>
          </Section>
          {/* Kept above the meter details: those arrive from WTM after the task has rendered, and would
              otherwise push the diagram out of view just as the user starts looking at it. */}
          {showRelationshipMap && (
            <Section>
              <TaskRelationsDiagram task={task} />
            </Section>
          )}

          {meterDetails && <MeterDetails meterInformation={meterDetails} projectNumber={task.projectNumber} />}

          <AccessConditions
            isAccessConditionsLoading={isAccessConditionsLoading}
            setShowAccessConditionsDialog={(value) =>
              dispatchTaskDetails({ type: 'set-show-access-conditions-dialog', payload: value })
            }
            accessConditions={task.accessConditions}
            task={task}
          />

          <ContactPersons workTaskId={task.id} contactPersons={contactPersons} addContactCallback={addContactCallback} />

          <Details
            task={task}
            cancelChanges={cancelChanges}
            isDirty={isDirty}
            workInvoice={workInvoice}
            workInvoiceProjectNumber={workInvoiceProjectNumber}
            editable={editable}
            toggleFieldIsDirty={toggleFieldIsDirty}
            isMeterTask={true}
          />

          {customerAppointment && (
            <CustomerAppointment editable={editable} toggleFieldIsDirty={toggleFieldIsDirty} isMeterTask />
          )}

          {components &&
            components.map((c: TaskComponentDTO) => (
              <TaskTypeTemplateComponent
                component={c as TaskComponent}
                onChange={handleChangeDynamicField}
                mode={editable ? 'write' : 'read'}
                key={c.id}
                showError={dynamicFieldErrors}
              />
            ))}
          {editable && (
            <StyledFooter>
              <Button onClick={() => cancelChanges()} variant="secondary" data-testid="cancel-update-task-button">
                {<div>Annuller</div>}
              </Button>
              <Button onClick={() => saveUpdatedTask()} data-testid="confirm-update-task-button" disabled={!isDirty}>
                {isUpdatingTask ? <StyledCircularProgress size={16} /> : <div>Gem</div>}
              </Button>
            </StyledFooter>
          )}

          <ExternalResources externalLinks={task?.taskType?.externalResources} />
          <Section>
            <NearbyTasks
              assetId={task?.assetId ?? ''}
              coordinates={{
                longitude: task?.taskLocation?.longitude ?? 0,
                latitude: task?.taskLocation?.latitude ?? 0
              }}
              workTaskId={task?.id}
            />
          </Section>
        </Container>
      </MeterTaskDetailsDispatchContext.Provider>
    </MeterTaskDetailsContext.Provider>
  );
};

const dialogBody: DialogBody = {
  headerText: 'Vil du annullere dine ændringer til arbejdskortet?',
  bodyText: '',
  declineButtonText: 'Fortryd',
  confirmButtonText: 'Bekræft'
};

export const StyledSectionContent = styled((props) => <SectionContent {...props} direction="row" />)`
  div {
    row-gap: 24px;
  }
`;

export const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 24px 12px 4px 12px;

  position: sticky;
  bottom: 0;

  z-index: ${(props) => props.theme.zIndex.main};
  border-top: 1px solid ${(props) => props.theme.palette.grey.black10};
  background-color: white;
  && > * {
    margin-left: 12px;
  }
`;

export default MeterTaskDetailsStep;
